/**
 * ============================================================================
 * LINK GENERATOR SERVICE - Frontend
 * ============================================================================
 * 
 * Frontend utilities for transaction link generation, sharing, and QR codes.
 * Handles short links, social sharing, and link analytics.
 */

import api from './api';

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    return error.response.data;
  }
  return { message: error.message || 'An error occurred' };
};

/**
 * Get transaction short link
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<string>} Full short link URL
 */
export const getTransactionShortLink = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/short-link`);
    return response.data.link;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get short link URL from short code
 * @param {string} shortCode - Short code (e.g. ABC123)
 * @returns {Promise<Object>} Axios-style response with data: full short link URL
 */
export const getShortLink = async (shortCode) => {
  try {
    if (!shortCode) {
      return { data: '' };
    }
    const base = typeof window !== 'undefined'
      ? `${window.location.origin}`
      : (import.meta.env.VITE_APP_URL || 'https://wekatrust.com');
    const path = shortCode.startsWith('http') ? shortCode : `${base}/t/${shortCode}`;
    return { data: path };
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Track link view (when someone opens the short link)
 * @param {string} shortCodeOrTransactionId - Short code or transaction ID
 * @param {Object} metadata - Optional metadata (userAgent, referrer, etc.)
 * @returns {Promise<void>}
 */
export const trackLinkView = async (shortCodeOrTransactionId, metadata = {}) => {
  try {
    if (!shortCodeOrTransactionId) return;
    await api.post(`/transactions/${shortCodeOrTransactionId}/track-view`, metadata);
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.warn('Failed to track link view:', error?.message || error);
    }
  }
};

/**
 * Generate short link for transaction
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<string>} Generated short link
 */
export const generateShortLink = async (transactionId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/short-link`);
    return response.data.link;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get QR code for transaction
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<string>} QR code image URL
 */
export const getQRCode = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/qr-code`);
    return response.data.qrCodeUrl;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get social sharing links for transaction
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<Object>} Social sharing links (whatsapp, facebook, twitter, etc.)
 */
export const getSocialSharingLinks = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/social-links`);
    return response.data.links;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get social metadata for transaction (Open Graph, Twitter Card)
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<Object>} Social metadata
 */
export const getSocialMetadata = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/social-metadata`);
    return response.data.metadata;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Track link share
 * @param {string} transactionId - Transaction ID
 * @param {string} channel - Sharing channel (whatsapp, email, etc.)
 * @returns {Promise<void>}
 */
export const trackLinkShare = async (transactionId, channel) => {
  try {
    await api.post(`/transactions/${transactionId}/track-share`, {
      channel,
    });
  } catch (error) {
    // Don't throw - tracking failure shouldn't break the UI
    console.warn('Failed to track link share:', error);
  }
};

/**
 * Get link view count
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<number>} View count
 */
export const getLinkViewCount = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/view-count`);
    return response.data.count;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get share analytics
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<Object>} Share analytics
 */
export const getShareAnalytics = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/share-analytics`);
    return response.data.analytics;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get link statistics (views + share analytics)
 * @param {string} transactionIdOrShortCode - Transaction ID or short code
 * @returns {Promise<Object>} Axios-style response with data: { viewCount, ...analytics }
 */
export const getLinkStats = async (transactionIdOrShortCode) => {
  try {
    const id = transactionIdOrShortCode;
    const [viewRes, analyticsRes] = await Promise.all([
      api.get(`/transactions/${id}/view-count`).catch(() => ({ data: { count: 0 } })),
      api.get(`/transactions/${id}/share-analytics`).catch(() => ({ data: { analytics: {} } })),
    ]);
    const viewCount = viewRes.data?.count ?? 0;
    const analytics = analyticsRes.data?.analytics ?? {};
    return { data: { viewCount, views: viewCount, ...analytics } };
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Regenerate short link (security feature)
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<string>} New short link
 */
export const regenerateShortLink = async (transactionId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/regenerate-link`);
    return response.data.link;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Share link via WhatsApp
 * @param {string} transactionId - Transaction ID
 * @param {string} phoneNumber - Phone number (optional)
 * @returns {Promise<void>}
 */
export const shareViaWhatsApp = async (transactionId, phoneNumber = null) => {
  try {
    const links = await getSocialSharingLinks(transactionId);
    const whatsappLink = phoneNumber
      ? `https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(links.whatsapp)}`
      : links.whatsapp;

    window.open(whatsappLink, '_blank');
    await trackLinkShare(transactionId, 'whatsapp');
  } catch (error) {
    console.error('Failed to share via WhatsApp:', error);
  }
};

/**
 * Share link via Email
 * @param {string} transactionId - Transaction ID
 * @param {string} email - Email address
 * @param {string} subject - Email subject
 * @param {string} body - Email body
 * @returns {Promise<void>}
 */
export const shareViaEmail = async (transactionId, email, subject, body) => {
  try {
    const links = await getSocialSharingLinks(transactionId);
    const emailLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body || links.email)}`;

    window.location.href = emailLink;
    await trackLinkShare(transactionId, 'email');
  } catch (error) {
    console.error('Failed to share via email:', error);
  }
};

/**
 * Copy link to clipboard
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<void>}
 */
export const copyLinkToClipboard = async (transactionId) => {
  try {
    const link = await getTransactionShortLink(transactionId);
    await navigator.clipboard.writeText(link);
    await trackLinkShare(transactionId, 'clipboard');
  } catch (error) {
    console.error('Failed to copy link:', error);
    throw error;
  }
};

export default {
  getTransactionShortLink,
  generateShortLink,
  getQRCode,
  getSocialSharingLinks,
  getSocialMetadata,
  trackLinkShare,
  getLinkViewCount,
  getShareAnalytics,
  getLinkStats,
  getShortLink,
  trackLinkView,
  regenerateShortLink,
  shareViaWhatsApp,
  shareViaEmail,
  copyLinkToClipboard,
};
